import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import {
  DccConfigurationCreateFeaturePayload,
  DccConfigurationFeatureFlag,
  DccConfigurationFeatureFlagResponse,
  DccConfigurationFlagsResponse,
  normalizeDccConfigurationFeatureFlag,
  normalizeDccConfigurationFeatureFlags,
} from '../../models/dcc-configuration.model';

@Injectable({
  providedIn: 'root',
})
export class DccConfigurationService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = 'v1/features/flags';

  getFeatureFlags(): Observable<DccConfigurationFeatureFlag[]> {
    return this.http
      .get<DccConfigurationFlagsResponse>(this.baseUrl)
      .pipe(
        map((response) => normalizeDccConfigurationFeatureFlags(response.data)),
      );
  }

  createFeature(
    payload: DccConfigurationCreateFeaturePayload,
  ): Observable<DccConfigurationFeatureFlag> {
    return this.http
      .post<DccConfigurationFeatureFlagResponse>(this.baseUrl, payload)
      .pipe(
        map((response) =>
          normalizeDccConfigurationFeatureFlag(response.data, {
            name: payload.name,
            description: payload.description,
            enabled: payload.enabled,
            groups: payload.groups.map((group) => ({
              name: group.name,
              rollout_percentage: group.rolloutPercentage,
            })),
          }),
        ),
      );
  }

  addGroupToFeature(
    featureName: string,
    dccCode: string,
  ): Observable<DccConfigurationFeatureFlag> {
    return this.http
      .post<DccConfigurationFeatureFlagResponse>(
        `${this.baseUrl}/${encodeURIComponent(featureName)}/groups`,
        {
          name: dccCode,
          rolloutPercentage: 100,
        },
      )
      .pipe(
        map((response) =>
          normalizeDccConfigurationFeatureFlag(response.data, {
            name: featureName,
            description: response.data?.description ?? '',
            enabled: response.data?.enabled ?? true,
            groups: [{ name: dccCode, rollout_percentage: 100 }],
          }),
        ),
      );
  }

  removeGroupFromFeature(
    featureName: string,
    dccCode: string,
  ): Observable<void> {
    return this.http.delete<void>(
      `${this.baseUrl}/${encodeURIComponent(featureName)}/groups/${encodeURIComponent(dccCode)}`,
    );
  }
}
